import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { STAGE_DEFINITIONS, type GarmentStageFlowView, type WorkflowStage } from '@tailoring-crm/shared'; // prettier-ignore
import { EmptyState } from '../components/feedback/empty-state.js';
import { PageHeader } from '../components/layout/page-header.js';
import { Button, ListSkeleton, useToast } from '@tailoring-crm/ui';
import { isApiRequestError } from '../lib/api/errors.js';
import { useTemplates } from '../lib/measurements/use-templates.js';
import { useStageConfiguration, useUpdateStageFlows } from '../lib/workflow/use-kanban.js';
import { Shirt } from 'lucide-react';

/**
 * Which of the shop's stages each garment walks.
 *
 * A blouse and a gents pant do not take the same route — the pant goes to ironing, the blouse
 * goes to handwork — and a shop forced to run one flow for both either carries stages that
 * never apply or loses ones that do.
 *
 * TWO THINGS THIS SCREEN DELIBERATELY DOES NOT DO.
 *
 * It does not let a stage be REORDERED. The canonical order is what makes "forward" and
 * "backward" mean anything: the stage engine, the rework rules and every per-stage report are
 * written against it, and a shop that could put finishing before cutting would break all three.
 * So a garment chooses WHICH stages it walks; the order is the product's.
 *
 * It does not offer the stages the shop has switched off. This screen narrows the shop's
 * workflow; it cannot widen it. A shop that wants ironing for its trousers turns ironing on
 * first, in the workflow settings, and then switches it off for everything else here.
 *
 * A garment with every stage ticked has no flow of its own and is stored as nothing at all —
 * which is what keeps it following the shop's workflow when that workflow later changes.
 */

/** Rows keyed by garment type. A garment absent from the map walks the shop's whole workflow. */
type Draft = Record<string, ReadonlySet<WorkflowStage>>;

function draftFrom(
  flows: readonly GarmentStageFlowView[],
  garmentTypeKeys: readonly string[],
  enabledStages: readonly WorkflowStage[],
): Draft {
  const byGarment = new Map(flows.map((flow) => [flow.garmentTypeKey, new Set(flow.stages)]));
  const draft: Draft = {};
  for (const key of garmentTypeKeys) {
    // No stored flow means "walks everything", which is what the ticks should show.
    draft[key] = byGarment.get(key) ?? new Set(enabledStages);
  }
  return draft;
}

export function StageFlowsPage(): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();

  const configuration = useStageConfiguration();
  const templates = useTemplates();
  const save = useUpdateStageFlows();

  const [draft, setDraft] = useState<Draft | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  const enabledStages = configuration.data?.enabledStages ?? [];
  const garments = templates.data?.templates ?? [];
  /**
   * Only the stages a shop may switch off per garment. `booked`, `delivered` and `cancelled`
   * are required for the whole product — every garment is booked and every garment ends — so
   * offering a tick that cannot be unticked would be a control that does nothing.
   */
  const choosable = enabledStages.filter((stage) => !STAGE_DEFINITIONS[stage].required);

  if (configuration.isLoading || templates.isLoading) {
    return (
      <>
        <PageHeader title={t('settings.stageFlows.title')} />
        <ListSkeleton rows={4} />
      </>
    );
  }

  if (garments.length === 0) {
    return (
      <>
        <PageHeader
          title={t('settings.stageFlows.title')}
          description={t('settings.stageFlows.description')}
        />
        <EmptyState
          icon={Shirt}
          title={t('settings.stageFlows.noGarmentsTitle')}
          description={t('settings.stageFlows.noGarmentsDescription')}
        />
      </>
    );
  }

  const rows =
    draft ??
    draftFrom(
      configuration.data?.flows ?? [],
      garments.map((garment) => garment.garmentTypeKey),
      enabledStages,
    );

  const toggle = (garmentTypeKey: string, stage: WorkflowStage): void => {
    setRefusal(null);
    const current = new Set(rows[garmentTypeKey] ?? enabledStages);
    if (current.has(stage)) current.delete(stage);
    else current.add(stage);
    setDraft({ ...rows, [garmentTypeKey]: current });
  };

  const labelOf = (stage: WorkflowStage): string =>
    configuration.data?.stages.find((one) => one.key === stage)?.label ?? t(`stage.${stage}`);

  const submit = async (): Promise<void> => {
    setRefusal(null);

    // A garment that walks every enabled stage is stored as NO flow. Writing it out would
    // freeze it against today's workflow, so a stage the shop adds later would silently fail
    // to reach it — the opposite of what "I did not customise this one" means.
    const payload: GarmentStageFlowView[] = [];
    for (const [garmentTypeKey, stages] of Object.entries(rows)) {
      const chosen = choosable.filter((stage) => stages.has(stage));
      if (chosen.length === choosable.length) continue;
      payload.push({ garmentTypeKey, stages: chosen });
    }

    try {
      const saved = await save.mutateAsync(payload);
      setDraft(
        draftFrom(
          saved.flows,
          garments.map((garment) => garment.garmentTypeKey),
          saved.enabledStages,
        ),
      );
      toast({ title: t('settings.stageFlows.saved'), variant: 'success' });
    } catch (caught) {
      setRefusal(isApiRequestError(caught) ? caught.message : t('error.unexpected'));
    }
  };

  return (
    <>
      <PageHeader
        title={t('settings.stageFlows.title')}
        description={t('settings.stageFlows.description')}
      />

      <div className="flex flex-col gap-4">
        {refusal ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
          >
            {refusal}
          </p>
        ) : null}

        <ul className="flex flex-col gap-3">
          {garments.map((garment) => {
            const stages = rows[garment.garmentTypeKey] ?? new Set(enabledStages);
            return (
              <li
                key={garment.garmentTypeKey}
                className="flex flex-col gap-2 rounded-lg border border-border bg-card p-3"
              >
                <p className="font-medium">{garment.garmentTypeLabel}</p>
                {/* Wraps rather than scrolls: a shop reads this as one shape per garment, and
                    a row that scrolled sideways would hide the stage somebody came to find. */}
                <div className="flex flex-wrap gap-2">
                  {choosable.map((stage) => {
                    const on = stages.has(stage);
                    return (
                      <label
                        key={stage}
                        className="flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() => toggle(garment.garmentTypeKey, stage)}
                          aria-label={t('settings.stageFlows.toggle', {
                            stage: labelOf(stage),
                            garment: garment.garmentTypeLabel,
                          })}
                        />
                        {labelOf(stage)}
                      </label>
                    );
                  })}
                </div>
              </li>
            );
          })}
        </ul>

        <div>
          <Button onClick={() => void submit()} disabled={save.isPending}>
            {t('common.save')}
          </Button>
        </div>
      </div>
    </>
  );
}

export default StageFlowsPage;
